import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Notifications = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, jobs, bids, bookings
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 100;

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/notifications?limit=${PAGE_SIZE}`);
      setNotifications(response.data);
      setHasMore((response.data || []).length === PAGE_SIZE);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreNotifications = async () => {
    if (loadingMore || notifications.length === 0) return;
    setLoadingMore(true);
    try {
      const cursor = notifications[notifications.length - 1]?.created_at;
      const response = await axios.get(`${API}/notifications?limit=${PAGE_SIZE}&cursor=${encodeURIComponent(cursor)}`);
      const page = response.data || [];
      setNotifications(prev => [...prev, ...page]);
      setHasMore(page.length === PAGE_SIZE);
    } catch (error) {
      console.error('Error loading more notifications:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      setNotifications(prev => prev.map(n =>
        n.id === notificationId ? { ...n, read: true } : n
      ));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/mark-all-read`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await axios.delete(`${API}/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Are you sure you want to delete all notifications?')) return;
    try {
      await axios.delete(`${API}/notifications/clear-all`);
      setNotifications([]);
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  };

  const handleNotificationAction = async (notification) => {
    // Mark as read first
    if (!notification.read) {
      await handleMarkAsRead(notification.id);
    }

    // Navigate based on notification type with deep linking
    switch (notification.type) {
      // Job-related notifications
      case 'new_job_in_category':
        if (notification.job_id) {
          navigate(`/jobs?highlight=${notification.job_id}`);
        } else {
          navigate('/jobs');
        }
        break;
        
      // Bid notifications
      case 'new_bid':
        // Client received a new bid on their job
        if (notification.job_id) {
          navigate(`/dashboard?view_bids=${notification.job_id}`);
        } else {
          navigate('/dashboard');
        }
        break;
        
      case 'bid_accepted':
        // Provider's bid was accepted - go to my bids or job conversation
        if (notification.conversation_id && notification.job_id) {
          navigate(`/dashboard?job_chat=${notification.job_id}`);
        } else {
          navigate('/dashboard?tab=my-bids');
        }
        break;
        
      case 'bid_rejected':
        navigate('/dashboard?tab=my-bids');
        break;
        
      case 'bid_inquiry':
        // Bidder received a question about their bid
        if (notification.inquiry_id) {
          navigate(`/inquiries/${notification.inquiry_id}`);
        } else {
          navigate('/inquiries');
        }
        break;
        
      // Booking notifications
      case 'booking_request':
        // Provider received a booking request
        if (notification.booking_id) {
          navigate(`/dashboard?booking=${notification.booking_id}`);
        } else {
          navigate('/dashboard?tab=bookings');
        }
        break;
        
      case 'booking_accepted':
      case 'booking_rejected':
        // Client's booking was accepted/rejected
        if (notification.booking_id) {
          navigate(`/dashboard?booking=${notification.booking_id}`);
        } else {
          navigate('/dashboard?tab=bookings');
        }
        break;
        
      case 'booking_completed':
        // Booking was marked complete - may need to review or pay
        if (notification.booking_id) {
          navigate(`/dashboard?booking=${notification.booking_id}&action=complete`);
        } else {
          navigate('/dashboard?tab=bookings');
        }
        break;
        
      case 'booking_completion_requested':
        // Provider requested completion - client needs to confirm
        if (notification.booking_id) {
          navigate(`/dashboard?booking=${notification.booking_id}&action=confirm`);
        } else {
          navigate('/dashboard?tab=bookings');
        }
        break;
        
      // Payment notifications
      case 'escrow_released':
      case 'payment_received':
        navigate('/earnings');
        break;
        
      case 'payment_pending':
        if (notification.booking_id) {
          navigate(`/payment/${notification.booking_id}`);
        } else {
          navigate('/payments');
        }
        break;
        
      // Message/Inquiry notifications
      case 'new_inquiry':
      case 'service_inquiry':
      case 'inquiry_reply':
      case 'new_message':
        if (notification.inquiry_id) {
          navigate(`/inquiries/${notification.inquiry_id}`);
        } else {
          navigate('/inquiries');
        }
        break;
        
      // Job conversation messages
      case 'job_message':
        if (notification.job_id) {
          navigate(`/dashboard?job_chat=${notification.job_id}`);
        } else {
          navigate('/inquiries');
        }
        break;
        
      // Review notifications
      case 'review_received':
        if (notification.service_id) {
          navigate(`/service/${notification.service_id}`);
        } else {
          navigate('/dashboard');
        }
        break;
        
      case 'review_request':
        if (notification.booking_id) {
          navigate(`/dashboard?booking=${notification.booking_id}&action=review`);
        } else {
          navigate('/dashboard');
        }
        break;

      // Dispute notifications
      case 'dispute_opened':
      case 'dispute_message':
      case 'dispute_response':
      case 'dispute_escalated':
      case 'dispute_resubmission':
      case 'dispute_resolved':
        if (notification.dispute_id) {
          navigate(`/disputes/${notification.dispute_id}`);
        } else {
          navigate('/dashboard');
        }
        break;
        
      // Owner announcement — open its link if one was attached
      case 'announcement':
        if (notification.link_url) {
          const url = notification.link_url;
          if (/^https?:\/\//i.test(url)) {
            window.open(url, '_blank', 'noopener');
          } else {
            navigate(url.startsWith('/') ? url : `/${url}`);
          }
        }
        break;

      // KYC / ID verification submissions — admins go to ID Verification Management
      case 'kyc_submission':
      case 'kyc_resubmission':
      case 'kyc_pending':
      case 'kyc_verification_needed':
        navigate('/admin/id-verification');
        break;

      // Default fallback
      default:
        if (notification.action_url) {
          navigate(notification.action_url);
        } else if (notification.inquiry_id) {
          navigate(`/inquiries/${notification.inquiry_id}`);
        } else if (notification.booking_id) {
          navigate(`/dashboard?booking=${notification.booking_id}`);
        } else if (notification.job_id) {
          navigate(`/dashboard?job=${notification.job_id}`);
        } else {
          navigate('/dashboard');
        }
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_job_in_category': return '💼';
      case 'new_bid': return '💰';
      case 'bid_accepted': return '✅';
      case 'bid_rejected': return '❌';
      case 'bid_inquiry': return '❓';
      case 'booking_request': return '📅';
      case 'booking_accepted': return '✅';
      case 'booking_rejected': return '❌';
      case 'booking_completed': return '🎉';
      case 'booking_completion_requested': return '⏳';
      case 'escrow_released': return '💵';
      case 'payment_received': return '💵';
      case 'payment_pending': return '💳';
      case 'new_inquiry': return '💬';
      case 'service_inquiry': return '💬';
      case 'inquiry_reply': return '💬';
      case 'new_message': return '💬';
      case 'job_message': return '💬';
      case 'review_received': return '⭐';
      case 'review_request': return '⭐';
      case 'dispute_opened': return '⚖️';
      case 'dispute_message': return '⚖️';
      case 'dispute_response': return '⚖️';
      case 'dispute_escalated': return '🚨';
      case 'dispute_resubmission': return '🔄';
      case 'dispute_resolved': return '✅';
      case 'announcement': return '📢';
      default: return '🔔';
    }
  };

  const getActionLabel = (notification) => {
    switch (notification.type) {
      case 'new_job_in_category': return 'View Job';
      case 'new_bid': return 'View Bids';
      case 'bid_accepted': return 'Message Client';
      case 'bid_rejected': return 'View My Bids';
      case 'bid_inquiry': return 'Reply to Question';
      case 'booking_request': return 'View Request';
      case 'booking_accepted': return 'View Booking';
      case 'booking_rejected': return 'View Details';
      case 'booking_completed': return 'View Completion';
      case 'booking_completion_requested': return 'Confirm Completion';
      case 'escrow_released': return 'View Earnings';
      case 'payment_received': return 'View Payment';
      case 'payment_pending': return 'Complete Payment';
      case 'new_inquiry': return 'Reply';
      case 'service_inquiry': return 'Reply';
      case 'inquiry_reply': return 'View Message';
      case 'new_message': return 'View Message';
      case 'job_message': return 'View Chat';
      case 'review_received': return 'View Review';
      case 'review_request': return 'Leave Review';
      case 'announcement': return notification.link_label || t('notifications.learnMore');
      default: return notification.action_label || t('notifications.viewDetails');
    }
  };

  const getNotificationCategory = (type) => {
    if (type?.includes('job')) return 'jobs';
    if (type?.includes('bid')) return 'bids';
    if (type?.includes('booking') || type?.includes('escrow')) return 'bookings';
    return 'other';
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    // Less than 1 minute
    if (diff < 60000) return t('notifications.justNow');
    // Less than 1 hour
    if (diff < 3600000) return t('notifications.minAgo', { count: Math.floor(diff / 60000) });
    // Less than 24 hours
    if (diff < 86400000) return t('notifications.hoursAgo', { count: Math.floor(diff / 3600000) });
    // Less than 7 days
    if (diff < 604800000) return t('notifications.daysAgo', { count: Math.floor(diff / 86400000) });
    // Otherwise show date
    return date.toLocaleDateString('en-PH', { month: 'short', day: 'numeric' });
  };

  // Filter notifications
  const filteredNotifications = notifications.filter(n => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !n.read;
    return getNotificationCategory(n.type) === filter;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  // Role-aware tabs + quick actions. Provider browses Jobs (to bid); Client browses
  // Services (to book). Admin only gets All/Unread.
  const effectiveRole = user?.active_role || user?.role || 'client';
  const baseTabs = [
    { id: 'all', label: t('notifications.all') },
    { id: 'unread', label: `${t('notifications.unread')} (${unreadCount})` },
  ];
  let roleTabs = [];
  if (effectiveRole === 'provider') {
    roleTabs = [
      { id: 'jobs', label: `💼 ${t('nav.browseJobs', 'Jobs')}` },
      { id: 'bids', label: `💰 ${t('nav.myBids', 'Bids')}` },
      { id: 'bookings', label: `📅 ${t('dashboard.myBookings', 'Bookings')}` },
    ];
  } else if (effectiveRole === 'client') {
    roleTabs = [
      { id: 'bids', label: `💰 ${t('nav.myBids', 'Bids')}` },
      { id: 'bookings', label: `📅 ${t('dashboard.myBookings', 'Bookings')}` },
    ];
  }
  const filterTabs = [...baseTabs, ...roleTabs];

  // If the active filter isn't available for the current role, fall back to "all".
  useEffect(() => {
    if (!filterTabs.some(t => t.id === filter)) {
      setFilter('all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveRole]);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="notifications-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">
              {t('notifications.title')}
            </h1>
            {unreadCount > 0 && (
              <p className="text-gray-600 mt-1">{unreadCount} {t('notifications.unread').toLowerCase()}</p>
            )}
          </div>
          <div className="flex gap-2 flex-shrink-0">
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-95 transition shadow-sm"
                data-testid="mark-all-read-btn"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
                {t('notifications.markAllRead')}
              </button>
            )}            {notifications.length > 0 && (
              <button
                onClick={handleClearAll}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-red-200 text-red-600 text-sm font-semibold hover:bg-red-50 active:scale-95 transition"
                data-testid="clear-all-btn"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                {t('notifications.clearAll')}
              </button>
            )}
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6 p-2 flex gap-2 overflow-x-auto" data-testid="notification-filters">
          {filterTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium transition whitespace-nowrap ${
                filter === tab.id 
                  ? 'bg-blue-600 text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid={`filter-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Notifications List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('notifications.loading')}</p>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="text-6xl mb-4">🔔</div>
            <h3 className="text-xl font-bold text-gray-700 mb-2">
              {filter === 'all' ? t('notifications.emptyAll') : t('notifications.emptyFiltered', { filter })}
            </h3>
            <p className="text-gray-500">
              {filter === 'all' 
                ? t('notifications.emptyHintAll')
                : t('notifications.emptyHintFiltered')}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                className={`bg-white rounded-lg shadow-md p-4 transition hover:shadow-lg cursor-pointer ${
                  !notification.read ? 'border-l-4 border-blue-500' : ''
                }`}
                onClick={() => handleNotificationAction(notification)}
                data-testid={`notification-${notification.id}`}
              >
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${
                    !notification.read ? 'bg-blue-100' : 'bg-gray-100'
                  }`}>
                    {getNotificationIcon(notification.type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h4 className={`font-semibold ${!notification.read ? 'text-gray-900' : 'text-gray-600'}`}>
                          {notification.title}
                        </h4>
                        <p className={`text-sm mt-1 ${!notification.read ? 'text-gray-700' : 'text-gray-500'}`}>
                          {notification.message}
                        </p>
                      </div>
                      <span className="text-xs text-gray-400 whitespace-nowrap">
                        {formatTime(notification.created_at)}
                      </span>
                    </div>

                    {/* Announcement image (if any) */}
                    {notification.type === 'announcement' && notification.image && (
                      <img
                        src={notification.image.startsWith('http') ? notification.image : `${process.env.REACT_APP_BACKEND_URL}${notification.image}`}
                        alt=""
                        className="mt-2 rounded-lg max-h-40 w-full object-cover"
                        data-testid={`notification-image-${notification.id}`}
                      />
                    )}

                    {/* Action Button — hidden for announcements with no link */}
                    {!(notification.type === 'announcement' && !notification.link_url) && (
                    <button
                      className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium inline-flex items-center gap-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNotificationAction(notification);
                      }}
                      data-testid={`action-btn-${notification.id}`}
                    >
                      {getActionLabel(notification)}
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    )}
                  </div>

                  {/* Delete Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(notification.id);
                    }}
                    className="text-gray-400 hover:text-red-500 p-1"
                    data-testid={`delete-notification-${notification.id}`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Unread indicator */}
                {!notification.read && (
                  <div className="flex justify-end mt-2">
                    <span className="text-xs text-blue-600 font-medium">● New</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {hasMore && filter === 'all' && (
          <div className="text-center mt-6">
            <button
              onClick={loadMoreNotifications}
              disabled={loadingMore}
              className="px-6 py-2 rounded-full border border-orange-200 text-orange-600 font-semibold hover:bg-orange-50 disabled:opacity-50 transition"
              data-testid="notifications-load-more"
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}

        {/* Quick Actions */}
        <div className="mt-8 bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-6">
          <h3 className="font-bold text-gray-800 mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/dashboard"
              className="bg-white border border-gray-200 px-4 py-2 rounded-lg hover:shadow-md transition font-medium text-gray-700"
              data-testid="quick-action-dashboard"
            >
              📊 Go to Dashboard
            </Link>
            {effectiveRole === 'provider' && (
              <Link
                to="/jobs"
                className="bg-white border border-gray-200 px-4 py-2 rounded-lg hover:shadow-md transition font-medium text-gray-700"
                data-testid="quick-action-browse-jobs"
              >
                💼 Browse Jobs
              </Link>
            )}
            {effectiveRole === 'client' && (
              <Link
                to="/services"
                className="bg-white border border-gray-200 px-4 py-2 rounded-lg hover:shadow-md transition font-medium text-gray-700"
                data-testid="quick-action-browse-services"
              >
                🛠️ Browse Services
              </Link>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Notifications;
